import React, { useState } from 'react'
import { Button, Card, Checkbox, Descriptions, Flex, Result, Select, Typography } from 'antd'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../../auth'
import { CONNECT_SITE } from '../../user/permission'
import { useUpdateActiveCompany } from '../../user/hooks'
import { ConnectRequest } from '../site'
import { useAuthorizeSite, useSiteLimit } from '../hooks'
import { canAddWebsite, ExtraWebsiteNotice } from './extra-website-notice'

export type ConnectSiteCardProps = {
    request: ConnectRequest
}

/**
 * The plugin can only receive the answer on its own WordPress admin; anything else is not sent back.
 */
function returnUrl(request: ConnectRequest, params: Record<string, string>): string | null {
    try {
        const site = new URL(request.siteUrl)
        const redirect = new URL(request.redirectUri)

        if (site.host !== redirect.host || !redirect.pathname.endsWith('/wp-admin/admin.php')) {
            return null
        }

        Object.entries(params).forEach(([key, value]) => redirect.searchParams.set(key, value))

        return redirect.toString()
    } catch {
        return null
    }
}

export function ConnectSiteCard({ request }: ConnectSiteCardProps) {
    const { t } = useTranslation()
    const { user, activeCompany, can } = useAuth()
    const updateActiveCompany = useUpdateActiveCompany()
    const authorize = useAuthorizeSite()
    const [candidatesInWordpress, setCandidatesInWordpress] = useState(true)
    const [confirmedExtra, setConfirmedExtra] = useState(false)
    const cancelUrl = returnUrl(request, { error: 'access_denied', state: request.state })
    const host = new URL(request.siteUrl).host
    const { data: limit } = useSiteLimit(can(CONNECT_SITE))
    // Connecting a website that is already connected again does not need a place.
    const alreadyConnected =
        limit !== undefined && limit.connectedHosts.includes(new URL(request.siteUrl).hostname.toLowerCase())

    if (!can(CONNECT_SITE)) {
        return (
            <Result
                status="403"
                title={t('You cannot connect websites')}
                subTitle={t('Ask an owner of your company to connect this website.')}
            />
        )
    }

    return (
        <Card className="mx-auto w-full max-w-xl">
            <Flex
                vertical
                gap="large"
            >
                <Flex
                    vertical
                    gap="small"
                >
                    <Typography.Title
                        level={3}
                        className="mb-0"
                    >
                        {t('Connect WordPress website')}
                    </Typography.Title>
                    <Typography.Text type="secondary">
                        {t('Evaa reads the public pages of this website. You can disconnect it at any time.')}
                    </Typography.Text>
                </Flex>

                <Descriptions
                    column={1}
                    bordered
                    size="small"
                    items={[
                        { key: 'name', label: t('Website'), children: request.siteName },
                        { key: 'host', label: t('Address'), children: host },
                        {
                            key: 'company',
                            label: t('Company'),
                            children:
                                user?.companies && user.companies.length > 1 ? (
                                    <Select
                                        className="w-full"
                                        value={activeCompany?.id}
                                        loading={updateActiveCompany.isPending}
                                        onChange={(companyId) => updateActiveCompany.mutate(companyId)}
                                        options={user.companies.map((company) => ({
                                            label: company.name,
                                            value: company.id,
                                        }))}
                                    />
                                ) : (
                                    activeCompany?.name
                                ),
                        },
                    ]}
                />

                <Flex vertical>
                    <Checkbox
                        checked={candidatesInWordpress}
                        onChange={(event) => setCandidatesInWordpress(event.target.checked)}
                    >
                        {t('Show candidates in WordPress (for administrators and editors)')}
                    </Checkbox>
                    <Typography.Text
                        type="secondary"
                        className="ml-6"
                    >
                        {t('Web agencies with access to the website can see them too. You can change this later.')}
                    </Typography.Text>
                </Flex>

                {!alreadyConnected && (
                    <ExtraWebsiteNotice
                        limit={limit}
                        confirmed={confirmedExtra}
                        onConfirmedChange={setConfirmedExtra}
                    />
                )}

                <Flex
                    justify="end"
                    gap="small"
                >
                    <Button
                        disabled={cancelUrl === null}
                        onClick={() => cancelUrl && (window.location.href = cancelUrl)}
                    >
                        {t('Cancel')}
                    </Button>
                    <Button
                        type="primary"
                        loading={authorize.isPending || authorize.isSuccess}
                        disabled={!alreadyConnected && !canAddWebsite(limit, confirmedExtra)}
                        onClick={() =>
                            authorize.mutate({ ...request, candidatesInWordpress, confirmExtraWebsite: confirmedExtra })
                        }
                    >
                        {t('Connect')}
                    </Button>
                </Flex>
            </Flex>
        </Card>
    )
}
